/**
 * Routing and role guards.
 *
 * Four apps share one bundle, so the route table is the place where "who can
 * see what" is decided. Two rules make it trustworthy:
 *
 *   1. Every route declares the roles allowed on it. A role landing somewhere
 *      it should not be is sent to its own home, not shown an empty screen.
 *
 *   2. The guard is a convenience, not the security boundary. Every endpoint
 *      checks permissions server-side; hiding a route only stops a user
 *      stumbling into a screen that would fail anyway. A customer cannot see
 *      another customer's data because the API refuses, not because the router
 *      does.
 *
 * Field and customer screens are loaded EAGERLY: a field phone on 4G at a shop
 * door must not wait on a chunk fetch to record a POD, and the service worker
 * caches JS cache-first on first fetch (it does not precache chunks), so the
 * field app has to be in the one cached bundle. ADMIN screens are code-split
 * (React.lazy) — no field user can reach them, admins open them online at a
 * desk, so keeping ~15k lines of admin code out of the field bundle is free.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, homeRouteFor, type RoleCode } from './lib/auth';
import { CartProvider } from './lib/cart';
import { Spinner } from './components/ui';
import { GlobalBusy } from './components/GlobalBusy';

// Auth
import Login from './screens/auth/Login';
import Register from './screens/auth/Register';

// Customer
import CustomerHome from './screens/customer/Home';
import Catalogue from './screens/customer/Catalogue';
import Cart from './screens/customer/Cart';
import Ledger from './screens/customer/Ledger';
import RaiseReturn from './screens/customer/RaiseReturn';
import { OrderList, OrderDetail, OrdersToDeliver, ConfirmDelivery } from './screens/customer/Orders';

// Salesperson
import Day from './screens/salesperson/Day';
import Beat, { Visit } from './screens/salesperson/Beat';
import { Collect, Collections, CollectionsTodayList } from './screens/salesperson/Collect';
import FieldStock from './screens/salesperson/Stock';
import Performance from './screens/salesperson/Performance';
import Summary from './screens/salesperson/Summary';

// Driver
import DriverRoutes, { RouteDetail } from './screens/driver/Routes';
import DriverDashboard from './screens/driver/Dashboard';
import { Pod, LoadIn } from './screens/driver/Pod';
import { ExtraBoxSale } from './screens/driver/ExtraBoxSale';
import LoadOutMyVan from './screens/driver/LoadOut';

// Admin — the one field-facing list stays eager (drivers open DeliveredTodayList
// from My Day); every other admin screen is code-split below.
import { PendingDeliveryList, DeliveredTodayList } from './screens/admin/DeliveryLists';

// Shared
import { CustomerList, CustomerDetail } from './screens/shared/Customers';
const Reminders = lazy(() => import('./screens/shared/Reminders'));
const Requirements = lazy(() => import('./screens/admin/Requirements'));
import MyProfile from './screens/shared/MyProfile';
import Targets from './screens/shared/Targets';
import { ThreadList, ThreadView } from './screens/chat/Chat';

/**
 * ADMIN SCREENS ARE CODE-SPLIT.
 *
 * Field and customer users never open these (every route below is ADMIN-only,
 * and no field screen imports one), so shipping them in the initial bundle only
 * made a field phone download ~15k lines of admin code it can't reach — and the
 * single >600 KB chunk. Each is now its own lazy chunk, fetched on first use.
 *
 * The field screens above stay EAGER on purpose: the service worker caches JS
 * cache-first on first fetch (it does not precache chunks), so a driver's POD or
 * Collect screen must already be in the one cached bundle to work at a door with
 * no signal. Admins open these at a desk, online, so a first-use chunk fetch is
 * fine — and the SW caches it for next time. See the Suspense boundary on Routes.
 */
const Dashboard = lazy(() => import('./screens/admin/Dashboard'));
const Approvals = lazy(() => import('./screens/admin/Approvals'));
const Stock = lazy(() => import('./screens/admin/Stock'));
const Reports = lazy(() => import('./screens/admin/Reports'));
const Settings = lazy(() => import('./screens/admin/Settings'));
const Settlements = lazy(() => import('./screens/admin/Settlements'));
const DispatchQueue = lazy(() => import('./screens/admin/DispatchQueue'));
const CollectionsSummary = lazy(() => import('./screens/admin/CollectionsSummary'));
const CustomerLedger = lazy(() => import('./screens/admin/CustomerLedger'));
const StaffDetail = lazy(() => import('./screens/admin/StaffDetail'));
const Purchases = lazy(() => import('./screens/admin/Purchases'));
const Loans = lazy(() => import('./screens/admin/Loans').then((m) => ({ default: m.Loans })));
const LoanDetail = lazy(() => import('./screens/admin/Loans').then((m) => ({ default: m.LoanDetail })));
const BankDeposits = lazy(() => import('./screens/admin/BankDeposits'));
const CreditNotes = lazy(() => import('./screens/admin/CreditNotes'));
const Expenses = lazy(() => import('./screens/admin/Expenses'));
const Manage = lazy(() => import('./screens/admin/Manage'));
const BusinessCentral = lazy(() => import('./screens/admin/BusinessCentral'));
const BcDrafts = lazy(() => import('./screens/admin/BcDrafts'));
const SalesInvoices = lazy(() => import('./screens/admin/SalesInvoices'));
const Products = lazy(() => import('./screens/admin/Products'));
const Attendance = lazy(() => import('./screens/admin/Attendance'));
const Allocations = lazy(() => import('./screens/admin/Allocations'));
const AdminMapView = lazy(() => import('./screens/admin/MapView'));
const FieldMapRoute = lazy(() => import('./screens/shared/MapRoute'));

const ALL_STAFF: RoleCode[] = ['owner_admin', 'manager', 'accountant', 'salesperson', 'driver', 'field_agent'];
const ADMIN: RoleCode[] = ['owner_admin', 'manager', 'accountant'];
const FIELD: RoleCode[] = ['salesperson', 'driver', 'field_agent'];

/** /map is one path, two screens: the admin proximity map, or the field route. */
function MapScreen() {
  const { user } = useAuth();
  return user && ADMIN.includes(user.role) ? <AdminMapView /> : <FieldMapRoute />;
}

export default function App() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Notification taps.
   *
   * The service worker cannot use the router, so it posts a path and this
   * listener performs the navigation. Registered once, at the top.
   */
  useEffect(() => {
    const onNavigate = (event: Event) => {
      const path = (event as CustomEvent<string>).detail;
      if (typeof path === 'string' && path.startsWith('/')) navigate(path);
    };
    window.addEventListener('gd:navigate', onNavigate);
    return () => window.removeEventListener('gd:navigate', onNavigate);
  }, [navigate]);

  // Restoring a session reads IndexedDB, so the first frame has no user yet.
  // Showing the shell before that resolves would flash the login screen at
  // someone who is already signed in.
  if (!ready) return <Spinner />;

  // See UpdateBanner. Rendered inside every branch below.

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        {/* Where they were going is remembered, so a deep link survives login. */}
        <Route
          path="*"
          element={<Navigate to="/login" replace state={{ from: location.pathname }} />}
        />
      </Routes>
    );
  }

  const home = homeRouteFor(user.role);

  return (
    <>
    <UpdateBanner />
    <GlobalBusy />
    <CartProvider>
      {/* Suspense boundary for the code-split admin screens above. Field screens
          are eager, so this fallback is only ever seen by an admin opening an
          admin screen for the first time. */}
      <Suspense fallback={<div style={{ padding: '3rem', display: 'grid', placeItems: 'center' }}><Spinner /></div>}>
      <Routes>
        {/* Already signed in: the auth screens are not somewhere to go back to. */}
        <Route path="/login" element={<Navigate to={home} replace />} />
        <Route path="/register" element={<Navigate to={home} replace />} />

        {/* ---------------------------------------------------------------- customer */}
        <Route path="/home" element={<Guard roles={['customer']}><CustomerHome /></Guard>} />
        {/* Ordering on behalf of a retailer.
            Opened up beyond `salesperson` because the API already permits it:
            `order.create_on_behalf` is granted to salesperson and driver, and
            `owner_admin` bypasses the permission check entirely. A distributor
            owner taking an order over the phone is an everyday event, and
            before this the only way to do it was to call the API by hand. */}
        <Route path="/catalogue" element={<Guard roles={['customer', 'salesperson', 'driver', 'field_agent', 'owner_admin']}><Catalogue /></Guard>} />
        <Route path="/cart" element={<Guard roles={['customer', 'salesperson', 'driver', 'field_agent', 'owner_admin']}><Cart /></Guard>} />
        <Route
          path="/returns/new/:invoiceId"
          element={<Guard roles={['customer', 'salesperson', 'driver', 'field_agent']}><RaiseReturn /></Guard>}
        />
        <Route
          path="/deliveries/:id/confirm"
          element={<Guard roles={['customer']}><ConfirmDelivery /></Guard>}
        />

        {/* Orders and payments are visible to the customer, their salesperson
            and the office — the shared Payment tab from the requirements. */}
        <Route path="/orders" element={<Guard roles={['customer', ...ALL_STAFF]}><OrderList /></Guard>} />
        {/* The driver's list of routed, undelivered orders — opened from the
            "Orders to Deliver" tile on the Orders screen. */}
        <Route path="/orders-to-deliver" element={<Guard roles={['driver', 'field_agent']}><OrdersToDeliver /></Guard>} />
        <Route path="/orders/:id" element={<Guard roles={['customer', ...ALL_STAFF]}><OrderDetail /></Guard>} />
        <Route path="/ledger" element={<Guard roles={['customer', ...ADMIN]}><Ledger /></Guard>} />

        {/* ------------------------------------------------------------- salesperson */}
        <Route path="/day" element={<Guard roles={FIELD}><Day /></Guard>} />
        <Route path="/beat" element={<Guard roles={['salesperson']}><Beat /></Guard>} />
        <Route path="/visit/:shopId" element={<Guard roles={['salesperson']}><Visit /></Guard>} />
        <Route path="/collect/:customerId" element={<Guard roles={FIELD}><Collect /></Guard>} />
        <Route path="/collections" element={<Guard roles={FIELD}><Collections /></Guard>} />
        <Route path="/collections-today" element={<Guard roles={FIELD}><CollectionsTodayList /></Guard>} />
        <Route path="/van-stock" element={<Guard roles={FIELD}><FieldStock /></Guard>} />
        <Route path="/performance" element={<Guard roles={['salesperson']}><Performance /></Guard>} />
        {/*
          The salesperson's own numbers by category, beat and customer.
          Admins get it too: the same three cuts are what they ask a
          salesperson for, and a screen only one role can open is a screen
          somebody screenshots and sends.
        */}
        <Route
          path="/my-summary"
          element={<Guard roles={['salesperson', ...ADMIN]}><Summary /></Guard>}
        />

        {/* ------------------------------------------------------------------ driver */}
        {/*
          The driver's dashboard, at /routes — the tab he already has.

          A new path with a new tab would have made five tabs on a phone, and
          the note on NAV says four or five is where they stop being reliably
          hittable with a thumb. The load-sheet list it replaces is one tap
          deeper, from the dashboard, which is where a driver with two sheets
          needs it and where a driver with one never has to go.
        */}
        <Route path="/routes" element={<Guard roles={['driver', 'field_agent']}><DriverDashboard /></Guard>} />
        <Route path="/load-sheets" element={<Guard roles={['driver', 'field_agent', ...ADMIN]}><DriverRoutes /></Guard>} />
        <Route path="/routes/:id" element={<Guard roles={['driver', 'field_agent', ...ADMIN]}><RouteDetail /></Guard>} />
        <Route path="/routes/:id/load-in" element={<Guard roles={['driver', 'field_agent', ...ADMIN]}><LoadIn /></Guard>} />
        {/* Admins are allowed too: a sale sometimes has to be entered for a
            driver who has no signal. The server still requires the load sheet
            to be the driver's own when a DRIVER is the caller. */}
        <Route
          path="/routes/:id/extra-box"
          element={<Guard roles={['driver', 'field_agent', ...ADMIN]}><ExtraBoxSale /></Guard>}
        />
        {/* The driver-cum-salesman loads his own van; the server restricts it to
            his own vehicle. Only field_agent — a plain driver accepts instead. */}
        <Route path="/load-out" element={<Guard roles={['field_agent', 'driver']}><LoadOutMyVan /></Guard>} />
        <Route path="/pod/:dispatchId" element={<Guard roles={['driver', 'field_agent']}><Pod /></Guard>} />
        {/*
          An on-call delivery: no route, no dispatch, addressed by INVOICE.

          Open to salespeople as well as drivers, per the client's rule of
          29 Sep that either may take an order and see it to the door. The
          server checks `delivery.record_pod` regardless; this only decides
          which screens a role can reach.
        */}
        <Route
          path="/pod/direct/:invoiceId"
          element={<Guard roles={['driver', 'field_agent', 'salesperson', ...ADMIN]}><Pod /></Guard>}
        />

        {/* ------------------------------------------------------------------- admin */}
        <Route path="/dashboard" element={<Guard roles={ADMIN}><Dashboard /></Guard>} />
        <Route path="/staff-details/:staffId" element={<Guard roles={ADMIN}><StaffDetail /></Guard>} />
        {/* One path, two screens: admin proximity map or the field route (MapScreen). */}
        <Route path="/map" element={<Guard roles={[...ADMIN, ...FIELD]}><MapScreen /></Guard>} />
        <Route path="/approvals" element={<Guard roles={ADMIN}><Approvals /></Guard>} />
        <Route path="/stock" element={<Guard roles={ADMIN}><Stock /></Guard>} />
        <Route path="/reports" element={<Guard roles={ADMIN}><Reports /></Guard>} />
        <Route path="/settlements" element={<Guard roles={ADMIN}><Settlements /></Guard>} />
        <Route path="/dispatch-queue" element={<Guard roles={ADMIN}><DispatchQueue /></Guard>} />
        <Route path="/collections-summary" element={<Guard roles={ADMIN}><CollectionsSummary /></Guard>} />
        <Route path="/pending-delivery" element={<Guard roles={ADMIN}><PendingDeliveryList /></Guard>} />
        <Route path="/delivered-today" element={<Guard roles={[...ADMIN, 'driver', 'field_agent']}><DeliveredTodayList /></Guard>} />
        <Route path="/customer-ledger" element={<Guard roles={ADMIN}><CustomerLedger /></Guard>} />

        {/* Purchases. Owner and manager only: raising an order commits money,
            and booking a receipt moves stock and creates an ITC claim. An
            accountant reads the result in Reports. */}
        <Route
          path="/purchases"
          element={<Guard roles={['owner_admin', 'manager']}><Purchases /></Guard>}
        />
        {/* Loans and EMI. The accountant is included deliberately: recording a
            repayment also writes the matching EMI expense, which is their
            side of the P&L. The endpoints are gated on loan.view/loan.manage,
            so the server still has the final say. */}
        <Route
          path="/loans"
          element={<Guard roles={['owner_admin', 'manager', 'accountant']}><Loans /></Guard>}
        />
        <Route
          path="/loans/:id"
          element={<Guard roles={['owner_admin', 'manager', 'accountant']}><LoanDetail /></Guard>}
        />
        {/* Bank deposits. The reconcile step is an accountant's job — it is
            the only place in the app where money is matched against a record
            the business does not write itself. Gated on bank.manage server
            side. */}
        <Route
          path="/bank-deposits"
          element={<Guard roles={['owner_admin', 'manager', 'accountant']}><BankDeposits /></Guard>}
        />
        {/* Credit notes and refunds. An accountant belongs here: paying out a
            refund is a finance action, and the credit note is the document
            their GST return is built from. */}
        {/* Expenses. An accountant belongs here — this is the cost side of the
            P&L, and without it there is no P&L. */}
        <Route
          path="/expenses"
          element={
            <Guard roles={['owner_admin', 'manager', 'accountant']}><Expenses /></Guard>
          }
        />
        <Route
          path="/credit-notes"
          element={
            <Guard roles={['owner_admin', 'manager', 'accountant']}><CreditNotes /></Guard>
          }
        />
        {/* Staff and master data — owner and manager only. An accountant has no
            business creating a driver. */}
        <Route
          path="/manage"
          element={<Guard roles={['owner_admin', 'manager']}><Manage /></Guard>}
        />
        {/* Accountant included: they are the ones who care whether the books
            in BC match what the app has sent. */}
        <Route
          path="/invoices"
          element={<Guard roles={ADMIN}><SalesInvoices /></Guard>}
        />
        <Route
          path="/bc-invoices"
          element={<Guard roles={ADMIN}><BcDrafts /></Guard>}
        />
        <Route
          path="/business-central"
          element={<Guard roles={ADMIN}><BusinessCentral /></Guard>}
        />
        {/* Prices and HSN codes: owner and manager only. An accountant reads the
            books, they do not set the selling price. */}
        <Route
          path="/products"
          element={<Guard roles={['owner_admin', 'manager']}><Products /></Guard>}
        />

        {/* Settings has a per-device tab that every role needs, so it is not
            admin-only; the server-side tabs simply fail to load for anyone
            without the settings permission. */}
        <Route path="/settings" element={<Settings />} />
        <Route path="/profile" element={<Guard roles={['salesperson', 'driver', 'field_agent', 'other_staff']}><MyProfile /></Guard>} />
        <Route path="/settings/:tab" element={<Settings />} />

        {/* ------------------------------------------------------------------ shared */}
        {/* Drivers included because they hold `order.create_on_behalf` and
            `customer.create`, and the customer list endpoint already scopes a
            driver to his own route plus shops he has served. Without this a
            driver tapping "New order" was bounced back to his home screen. */}
        <Route
          path="/customers"
          element={<Guard roles={['salesperson', 'driver', 'field_agent', ...ADMIN]}><CustomerList /></Guard>}
        />
        <Route
          path="/customers/:customerId"
          element={<Guard roles={['salesperson', 'driver', 'field_agent', ...ADMIN]}><CustomerDetail /></Guard>}
        />
        <Route path="/reminders" element={<Guard roles={ALL_STAFF}><Reminders /></Guard>} />
        <Route path="/requirements" element={<Guard roles={ADMIN}><Requirements /></Guard>} />
        <Route
          path="/targets"
          element={<Guard roles={['salesperson', 'owner_admin', 'manager']}><Targets /></Guard>}
        />
        {/*
          Daily attendance. Routed to the roles that supervise field staff;
          a salesperson has no business reading his colleagues' selfies.
        */}
        <Route
          path="/attendance"
          element={<Guard roles={ADMIN}><Attendance /></Guard>}
        />
        <Route
          path="/allocations"
          element={<Guard roles={['owner_admin', 'manager']}><Allocations /></Guard>}
        />

        <Route path="/chat" element={<Guard roles={['customer', ...ALL_STAFF, 'other_staff']}><ThreadList /></Guard>} />
        <Route
          path="/chat/:customerId"
          element={<Guard roles={['customer', ...ALL_STAFF, 'other_staff']}><ThreadView /></Guard>}
        />

        {/* Root and anything unknown go to this role's home. */}
        <Route path="/" element={<Navigate to={home} replace />} />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
      </Suspense>
    </CartProvider>
    </>
  );
}

/**
 * Role gate.
 *
 * Redirects rather than rendering a refusal: a driver who taps a stale
 * notification for an admin screen should land on his route list, not on a
 * dead end telling him off.
 */
function Guard({ roles, children }: { roles: RoleCode[]; children: JSX.Element }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeRouteFor(user.role)} replace />;
  return children;
}

/**
 * "A new version is available" — and the reason it exists.
 *
 * `main.tsx` has dispatched `gd:update-available` since the service worker was
 * written, and NOTHING has ever listened for it. The detection worked
 * perfectly and told no one.
 *
 * The consequence is not cosmetic. A PWA keeps running the JavaScript it
 * already has until the page is reloaded, so after a deploy somebody can be
 * looking at a screen whose code is hours old — saving with an old payload,
 * skipping a field that was added, hitting a bug that was fixed. During this
 * project's own cutover that cost two rounds of chasing a defect that had
 * already been corrected, with the database proving the fix worked and the
 * browser proving it did not.
 *
 * Deliberately NOT an automatic reload. Somebody may be halfway through an
 * invoice, and throwing that away to install an update is a worse failure than
 * the stale code. So: a bar they can act on, and ignore if they are busy.
 */
function UpdateBanner() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const onUpdate = () => setReady(true);
    window.addEventListener('gd:update-available', onUpdate);
    return () => window.removeEventListener('gd:update-available', onUpdate);
  }, []);

  if (!ready) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed', left: 0, right: 0, top: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
        padding: '12px 16px', background: 'var(--brand)', color: 'var(--brand-ink)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>A new version of the app is ready</span>
        <span style={{ fontSize: 12, opacity: 0.85 }}>Reload to get the latest — your current work is kept.</span>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          background: 'var(--brand-ink)', color: 'var(--brand)', border: 0,
          borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 14,
          cursor: 'pointer', flex: '0 0 auto',
        }}
      >
        Update now
      </button>
    </div>
  );
}
